import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { assistantApi, jobsApi } from "../../services/api";
import EmptyState from "../../components/EmptyState";
import AccessNotice, { UsageLeft } from "../../components/AccessNotice";
import { copyText, downloadResponse } from "../../utils/download";

function CoverLetterTab({ job, onNext }) {
  const { documents, updateJob, completeStep, toast, integrations } = useApp();
  const defaultTemplate = documents.templates.find((t) => t.isDefault) || documents.templates[0];
  const [templateId, setTemplateId] = useState(defaultTemplate?.id || "");
  const [text, setText] = useState(job.coverLetter || "");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [savingRequired, setSavingRequired] = useState(false);

  useEffect(() => setText(job.coverLetter || ""), [job.id, job.coverLetter]);

  const setNotRequired = async (noCoverLetter) => {
    setSavingRequired(true);
    await updateJob(job.id, { noCoverLetter });
    setSavingRequired(false);
  };

  if (job.noCoverLetter) {
    return (
      <div className="card">
        <EmptyState
          icon="bi-envelope-slash"
          title="Cover letter not required"
          text="This application doesn't take a cover letter, so this step is skipped. You can still write one if that changes."
          action={
            <div className="d-flex flex-wrap justify-content-center gap-2">
              <button className="btn btn-light btn-sm" onClick={() => setNotRequired(false)} disabled={savingRequired}>
                Write one anyway
              </button>
              <button className="btn btn-primary btn-sm" onClick={onNext}>
                Next: apply <i className="bi bi-arrow-right" />
              </button>
            </div>
          }
        />
      </div>
    );
  }

  const notRequiredButton = (
    <button className="btn btn-link btn-sm p-0 text-muted" onClick={() => setNotRequired(true)} disabled={savingRequired}>
      <i className="bi bi-envelope-slash me-1" />
      This application doesn't take a cover letter
    </button>
  );

  if (documents.templates.length === 0) {
    return (
      <div className="card">
        <EmptyState
          icon="bi-envelope-paper"
          title="Add a cover letter template first"
          text="We'll write a letter for this job in your own style, based on your template."
          action={
            <div className="d-flex flex-column align-items-center gap-2">
              <Link to="/documents#templates" className="btn btn-primary btn-sm">
                Add template
              </Link>
              {notRequiredButton}
            </div>
          }
        />
      </div>
    );
  }

  const generate = async () => {
    setLoading(true);
    try {
      const { letter } = await assistantApi.coverLetter(job.id, templateId);
      setText(letter);
    } catch (err) {
      toast(err.message, "danger");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    const saved = job.steps?.coverLetter
      ? await updateJob(job.id, { coverLetter: text })
      : await completeStep(job.id, "coverLetter", "Wrote cover letter", { coverLetter: text });
    if (saved) toast("Cover letter saved");
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { pages } = await downloadResponse(await jobsApi.coverLetterFile(job.id, text), "Cover_Letter.pdf");
      if (pages > 1) toast(`Your cover letter runs to ${pages} pages. Most recruiters expect one.`, "warning");
    } catch (err) {
      toast(err.message, "danger");
    } finally {
      setDownloading(false);
    }
  };

  const dirty = text !== (job.coverLetter || "");
  const saved = !!job.coverLetter && !dirty;

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2 mb-1">
          <h2 className="h6 mb-0">Cover letter</h2>
          {!job.coverLetter && notRequiredButton}
        </div>
        <p className="text-muted small mb-3">
          Written from your template, your resume and this job's description. Edit anything before saving.
        </p>

        <div className="row g-2 align-items-end mb-3">
          <div className="col-sm-8">
            <label className="form-label small fw-medium" htmlFor="cl-template">
              Template
            </label>
            <select id="cl-template" className="form-select" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              {documents.templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.isDefault ? " (default)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="col-sm-4 d-grid">
            <button
              className={`btn ${text ? "btn-outline-primary" : "btn-primary"}`}
              onClick={generate}
              disabled={loading || !integrations.ai}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" /> Writing…
                </>
              ) : (
                <>
                  <i className={`bi ${text ? "bi-arrow-repeat" : "bi-stars"} me-1`} /> {text ? "Rewrite" : "Write it for me"}
                </>
              )}
            </button>
            <UsageLeft className="form-text text-center mt-1" />
          </div>
        </div>

        {text ? (
          <>
            <textarea
              className="form-control letter-editor mb-3"
              rows={14}
              value={text}
              onChange={(e) => setText(e.target.value)}
              aria-label="Cover letter text"
            />
            <div className="d-flex flex-wrap gap-2">
              <button
                className="btn btn-light"
                onClick={async () => toast((await copyText(text)) ? "Copied to clipboard" : "Couldn't copy", "info")}
              >
                <i className="bi bi-clipboard me-1" /> Copy
              </button>
              <button className="btn btn-light" onClick={downloadPdf} disabled={downloading}>
                {downloading ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-file-earmark-pdf me-1" />}
                Download PDF
              </button>
              {saved ? (
                <button className="btn btn-primary ms-sm-auto" onClick={onNext}>
                  Next: apply <i className="bi bi-arrow-right" />
                </button>
              ) : (
                <button className="btn btn-primary ms-sm-auto" onClick={save}>
                  <i className="bi bi-check-lg me-1" /> Save cover letter
                </button>
              )}
            </div>
          </>
        ) : !integrations.ai ? (
          <AccessNotice feature="Writing cover letters" />
        ) : loading ? (
          <div className="text-center text-muted small py-4">Writing from your template, resume and the job description…</div>
        ) : (
          <div className="hint-box">
            <i className="bi bi-lightbulb" />
            <span>
              We keep your template's structure and voice, fill placeholders like <code>{"{{company}}"}</code> and{" "}
              <code>{"{{why_company}}"}</code>, and only mention experience from your resume.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default CoverLetterTab;
